/**
 * Horizontal Line — with a price label on the PRICE (y) axis.
 *
 * This deliberately OVERRIDES klinecharts' built-in `horizontalStraightLine`
 * (`registerOverlay` overwrites the registry by name — the same technique
 * `verticalLineOverlay.ts` / `horizontalRayOverlay.ts` document).
 *
 * The price label rides the y-axis gutter (via `createYAxisFigures`) as an
 * always-visible pill — matching the native last-price label and mirroring the
 * vertical line's time pill on the x-axis, so the two tools read as a symmetric
 * pair (price on the side axis, time on the bottom axis).
 *
 * The name stays `horizontalStraightLine` on purpose — it is the persisted key
 * (`SavedDrawing.name`); renaming would orphan every saved horizontal line.
 */
import { registerOverlay } from 'klinecharts'
import { CHART_COLORS, CHART_FONT_FAMILY } from './chartColors'
import { inkOn } from './tpoSettings'
import { isNum, type OverlayFigure } from './overlayUtils'
import { DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

/** Style the toolbar can override per drawing (lands on `overlay.styles.line`). */
export interface HorizontalLineStyle {
  color?: string
  size?: number
  style?: 'solid' | 'dashed'
}

/** Concrete pill background when the toolbar has set no line colour. */
const PILL_FALLBACK_COLOR = CHART_COLORS.textMuted

export interface HorizontalLineFigureInput {
  /** Pixel y of the line (from the overlay's single point). */
  y: number
  /** Pane width in pixels — the line spans it left to right. */
  width: number
  /** The toolbar's line colour, or undefined (line then inherits the theme). */
  color?: string
  /** Optional line width / dash from the toolbar. */
  size?: number
  lineStyle?: 'solid' | 'dashed'
  /** Text naming the level (`extendData.label`), drawn on the canvas. */
  label?: string
}

/** Gap between a level's label and the pane's right edge, and above the line. */
const LEVEL_LABEL_INSET = 6

/**
 * A level's label, on the canvas: on the line, right-aligned just inside the
 * pane's right edge (`rightX`), in the line's colour. The price axis carries
 * only prices (Chris, 2026-10-18); a label there read as a price. `''` or none
 * draws nothing.
 */
export function buildLevelLabelFigure(
  y: number,
  rightX: number,
  label: string | undefined,
  color?: string,
): OverlayFigure[] {
  if (!label || !isNum(y) || !isNum(rightX)) return []
  return [
    {
      key: 'level_label',
      type: 'text',
      ignoreEvent: true,
      attrs: {
        x: rightX - LEVEL_LABEL_INSET,
        y: y - 2,
        text: label,
        align: 'right',
        baseline: 'bottom',
      },
      styles: {
        style: 'fill',
        color: color ?? CHART_COLORS.text,
        size: 11,
        family: CHART_FONT_FAMILY,
        weight: 'normal',
        backgroundColor: 'transparent',
      },
    },
  ]
}

/** The label a level drawing carries in its `extendData`, or undefined. */
export function levelLabelOf(extendData: unknown): string | undefined {
  const label = (extendData as { label?: unknown } | null | undefined)?.label
  return typeof label === 'string' && label.trim() ? label : undefined
}

/**
 * Pure builder for the horizontal line, plus its label on the canvas when it
 * carries one (the PRICE rides the y-axis, see `buildHorizontalLineAxisFigures`).
 * Bails to `[]` on non-finite geometry (klinecharts would render garbage otherwise).
 */
export function buildHorizontalLineFigures(input: HorizontalLineFigureInput): OverlayFigure[] {
  const { y, width, color, size, lineStyle } = input
  if (!isNum(y) || !isNum(width)) return []

  const line: OverlayFigure[] = [
    {
      key: 'horizontal_line',
      type: 'line',
      attrs: {
        coordinates: [
          { x: 0, y },
          { x: width, y },
        ],
      },
      styles: {
        // Conditional, like horizontalRayOverlay: an unstyled line keeps the
        // theme overlay colour rather than being forced to a concrete value.
        ...(color ? { color } : {}),
        ...(size ? { size } : {}),
        ...(lineStyle === 'dashed' ? { style: 'dashed', dashedValue: [4, 4] } : {}),
      },
    },
  ]
  return [...line, ...buildLevelLabelFigure(y, width, input.label, color)]
}

/**
 * Pure builder for the y-axis (price) pill, at the line's y and left-aligned at
 * the axis pane's left edge (x=0) — where klinecharts anchors its own tick and
 * last-price labels. `''` label draws nothing. Text colour is DERIVED from the
 * pill background (inkOn) so a light line colour never renders illegible.
 */
export function buildHorizontalLineAxisFigures(
  y: number,
  label: string,
  color?: string,
): OverlayFigure[] {
  if (!isNum(y) || !label) return []
  const pillBg = color ?? PILL_FALLBACK_COLOR
  return [
    {
      type: 'text',
      ignoreEvent: true,
      attrs: { x: 0, y, text: label, align: 'left', baseline: 'middle' },
      styles: {
        style: 'fill',
        color: inkOn(pillBg),
        size: 11,
        // Explicit family: 'inherit' is invalid on a canvas and klinecharts
        // mis-measures the label box otherwise (see referencePriceLine).
        family: CHART_FONT_FAMILY,
        weight: 'normal',
        backgroundColor: pillBg,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 2,
      },
    },
  ]
}

/** Register the price-labelled horizontal line. Idempotent. */
export const registerHorizontalLineOverlay = registerOnce(() => {
  registerOverlay({
    name: 'horizontalStraightLine',
    totalStep: 2, // single click — one price point fully defines the line
    ...DEFAULT_FIGURES,
    // The default y-axis price pill (selected-only) is replaced by the
    // always-visible custom y-axis pill below.
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      bounding,
      overlay,
    }: {
      coordinates: Array<{ x: number; y: number }>
      bounding: { width: number }
      overlay: { styles?: { line?: HorizontalLineStyle }; extendData?: unknown }
    }) => {
      const c = coordinates[0]
      if (!c) return []
      const line = overlay.styles?.line ?? {}
      return buildHorizontalLineFigures({
        y: c.y,
        width: bounding.width,
        color: line.color,
        size: line.size,
        lineStyle: line.style === 'dashed' ? 'dashed' : 'solid',
        label: levelLabelOf(overlay.extendData),
      })
    },
    createYAxisFigures: ({
      coordinates,
      overlay,
      precision,
    }: {
      coordinates: Array<{ x: number; y: number }>
      overlay: { points: Array<{ value?: number }>; styles?: { line?: HorizontalLineStyle } }
      precision: { price: number }
    }) => {
      const c = coordinates[0]
      const value = overlay.points?.[0]?.value
      if (!c || value == null) return []
      const label = value.toFixed(precision.price)
      return buildHorizontalLineAxisFigures(c.y, label, overlay.styles?.line?.color)
    },
    // Dragging the line moves the level; it stays horizontal. Without this the
    // override would drop the built-in's drag behaviour.
    performEventPressedMove: ({
      points,
      performPoint,
    }: {
      points: Array<{ value?: number }>
      performPoint: { value?: number }
    }) => {
      points[0].value = performPoint.value
    },
  } as OverlayTemplateParam)
})
